import type { ChipProps } from '@mui/material';
import { Badge, Chip } from '@mui/material';
import { getSelectorAttribute, invoicesOverviewPageSelectors } from '@vy/app-selectors';
import type { InvoiceBillingStatus } from '@vy/types';
import { INVOICE_BILLING_STATUSES } from '@vy/types';
import { raiseUnreachable } from '@vy/utils';
import React from 'react';
import { VyTooltip } from '../../../../components/new-design/VyTooltip';

type InvoiceBillingStatusLabelProps = {
  billingStatus: InvoiceBillingStatus;
  isTrial?: boolean;
  tooltipContent?: React.ReactNode;
  showIndicator?: boolean;
} & Omit<ChipProps, 'label'>;

const TRIAL_CONFIG = {
  color: '#7A4FD0',
  backgroundColor: '#7A4FD014',
  label: 'Trial',
};

const CONFIG = {
  [INVOICE_BILLING_STATUSES.None]: {
    color: '#1A5BC8',
    backgroundColor: '#3A78F814',
    label: 'Open',
  },
  [INVOICE_BILLING_STATUSES.Paid]: {
    backgroundColor: '#01A45214',
    color: '#01A452',
    label: 'Paid',
  },
  [INVOICE_BILLING_STATUSES.Rejected]: {
    backgroundColor: '#E8EBF2',
    color: '#5F6978',
    label: 'Cancelled',
  },
  [INVOICE_BILLING_STATUSES.Overdue]: {
    backgroundColor: '#D32F2F14',
    color: '#C62828',
    label: 'Overdue',
  },
  [INVOICE_BILLING_STATUSES.PendingPayment]: {
    backgroundColor: '#FDF1D7',
    color: '#ED8822',
    label: 'Pending',
  },
};

export const InvoiceBillingStatusLabel: React.FC<InvoiceBillingStatusLabelProps> = ({
  billingStatus,
  isTrial = false,
  tooltipContent,
  showIndicator = false,
  ...props
}) => {
  // Trial contracts surface a "Trial" badge in place of the billing status, regardless of the invoice's status.
  const { color, label, backgroundColor } = isTrial ? TRIAL_CONFIG : CONFIG[billingStatus];

  const chip = (
    <Chip
      {...getSelectorAttribute(invoicesOverviewPageSelectors.columns.status)}
      label={label}
      sx={{
        backgroundColor,
        color,
        fontWeight: 500,
        fontSize: '0.875rem',
        ...props.sx,
      }}
    />
  );

  const badgedChip = !isTrial && showIndicator ? (
    <Badge
      badgeContent="!"
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{
        '& .MuiBadge-badge': {
          backgroundColor: '#ED8822',
          color: 'white',
          fontSize: '10px',
          fontWeight: 700,
          minWidth: 14,
          height: 14,
          padding: 0,
          borderRadius: '50%',
          top: 2,
          right: 4,
        },
      }}
    >
      {chip}
    </Badge>
  ) : (
    chip
  );

  return (
    <VyTooltip tooltipContent={isTrial ? undefined : tooltipContent} placement="top">
      {badgedChip}
    </VyTooltip>
  );
};

export function getBillingStatusLabel(billingStatus: InvoiceBillingStatus) {
  switch (billingStatus) {
    case INVOICE_BILLING_STATUSES.None:
      return 'Open';
    case INVOICE_BILLING_STATUSES.Paid:
      return 'Paid';
    case INVOICE_BILLING_STATUSES.Rejected:
      return 'Cancelled';
    case INVOICE_BILLING_STATUSES.PendingPayment:
      return 'Pending Payment';
    case INVOICE_BILLING_STATUSES.Overdue:
      return 'Overdue';
    default:
      return raiseUnreachable(billingStatus);
  }
}
